import { connect, ConnectedProps } from 'react-redux';
import { useParams } from 'react-router-dom-v5-compat';

import { FlagIssuesFormData } from 'dashboard/forms/FormFlagIssues';
import { reportIssueSuccessMessage } from 'dashboard/lib/constants/reportIssue';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { showMessage } from 'dashboard/redux/actions/messages';
import { clearVerification } from 'dashboard/redux/actions/verification';
import { RootState } from 'dashboard/redux/store';
import {
  flagVerification,
  loadVerification,
  redactVerification,
} from 'dashboard/redux/thunk-actions/verification';
import DashboardVerificationShowView from 'dashboard/views/DashboardVerificationsShowView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useInterval from 'lib/hooks/useInterval';
import useOnMountAndUnmount from 'lib/hooks/useOnMountAndUnmount';
import useRedirectOnEnvironmentChange from 'lib/hooks/useRedirectOnEnvironmentChange';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';
import { isVerificationInTerminalState } from 'lib/verification';

type Params = { verificationId: string };

const DashboardVerificationsShowRoute = (props: PropsFromRedux) => {
  const params = useParams<Params>() as Params;

  useOnMountAndUnmount(props.clearVerification);

  useRedirectOnEnvironmentChange('/dashboard/verifications');

  const { loading, callback: loadVerification } = useApiLoader({
    loader: () => props.loadVerification(params.verificationId),
    deps: [props.loadVerification, params.verificationId],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load verification.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { callback: pollVerification, error: pollVerificationError } = useApiCallback(async () => {
    props.loadVerification(params.verificationId);
  }, [props.loadVerification, params.verificationId]);

  useApiErrorHandler({
    error: pollVerificationError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to refresh verification.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues.', MessageType.Failure),
  });

  useInterval({
    callback: pollVerification,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    delay: !isVerificationInTerminalState(props.verification) ? 5000 : null,
  });

  const { error: redactVerificationError, callback: handleRedactVerification } = useApiCallback(
    async (verificationId: string) => {
      await props.redactVerification(verificationId);
      await loadVerification();
    },
    [props.redactVerification, loadVerification],
  );

  useApiErrorHandler({
    error: redactVerificationError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to redact verification, please try again.',
          MessageType.Failure,
          false,
        );
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'Failed to redact verification, please try again.',
        MessageType.Failure,
        false,
      ),
  });

  const { error: handleFlagIssuesError, callback: handleFlagIssues } = useApiCallback(
    async ({
      issueVerifications,
      issueType,
      fraudType,
      responseType,
      content,
      addAsFraudulent,
    }: FlagIssuesFormData) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      await props.flagVerification(props.verification, {
        issueVerifications,
        issueType,
        fraudType,
        responseType,
        content,
        addAsFraudulent,
      });
      props.showMessage(reportIssueSuccessMessage(responseType), MessageType.Success);
    },
    [props.verification, props.flagVerification],
  );

  useApiErrorHandler({
    error: handleFlagIssuesError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to report issue, please try again.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('Failed to report issue, please try again.', MessageType.Failure),
  });

  return (
    <DashboardVerificationShowView
      redactor={props.redactor}
      verification={props.verification}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      document={props.document}
      isLoading={loading}
      handleRedactVerification={handleRedactVerification}
      handleFlagIssues={handleFlagIssues}
      featureFlags={props.featureFlags}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    redactor: state.verification.redactor,
    verification: state.verification.verification,
    document: state.verification.document,
    featureFlags: state.featureFlags.featureFlags,
  };
};

const mapDispatchToProps = {
  clearVerification,
  loadVerification,
  redactVerification,
  flagVerification,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardVerificationsShowRoute);
